import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/utils';
import type { TransportLegState, TransportLegView } from '../model/transportPlan';

/**
 * Ikony dopravy — plné piktogramy z návrhu (ne obrysové lucide), aby box zůstal čitelný
 * i na 11 px u navazujících linek.
 */
function PlaneIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className="shrink-0"
    >
      <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
    </svg>
  );
}

function BusIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className="shrink-0"
    >
      <path d="M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4S4 2.5 4 6v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM18 11H6V6h12v5z" />
    </svg>
  );
}

/** Rámeček, výplň a barva textu podle vyplněnosti boxu. */
const STATE_BOX: Record<TransportLegState, string> = {
  full: 'border-solid border-phx-border-2 bg-phx-surface text-phx-text',
  partial: 'border-dashed border-phx-border-3 bg-phx-surface text-phx-text-2',
  draft: 'border-dashed border-phx-draft bg-phx-draft-tint text-phx-draft',
  missing: 'border-dashed border-phx-danger bg-phx-danger-tint text-phx-danger',
};

/** Půlkolečko navazující linky kopíruje rámeček i výplň svého boxu. */
const STATE_NOTCH: Record<TransportLegState, string> = {
  full: 'border-solid border-phx-border-2 bg-phx-surface',
  partial: 'border-dashed border-phx-border-3 bg-phx-surface',
  draft: 'border-dashed border-phx-draft bg-phx-draft-tint',
  missing: 'border-dashed border-phx-danger bg-phx-danger-tint',
};

function iconColor(leg: TransportLegView): string {
  if (leg.state === 'missing') return 'text-phx-danger';
  if (leg.state === 'draft') return 'text-phx-draft';
  return leg.mode === 'air' ? 'text-phx-info' : 'text-phx-warning';
}

function offsetLabel(offset: number): string | null {
  if (!offset) return null;
  return offset < 0 ? `−${Math.abs(offset)}d` : `+${offset}d`;
}

/** Sloupeček „kód + čas“ — u odjezdu zarovnaný doprava, u příjezdu doleva. */
function Endpoint({
  code,
  time,
  align,
}: {
  code: string;
  time: { label: string; known: boolean; offset: string | null };
  align: 'start' | 'end';
}) {
  return (
    <span
      className={cn(
        'flex min-w-0 flex-col gap-px leading-[1.1]',
        align === 'end' ? 'items-end' : 'items-start'
      )}
    >
      <span className="overflow-hidden font-semibold text-ellipsis">{code}</span>
      <span
        className={cn(
          'text-phx-text-muted flex items-center gap-0.5 tabular-nums',
          !time.known && 'opacity-50'
        )}
      >
        <span className="shrink-0">{time.label}</span>
        {time.offset && <span className="shrink-0">{time.offset}</span>}
      </span>
    </span>
  );
}

export interface TransportChipProps {
  leg: TransportLegView;
}

/**
 * Jeden box dopravy: první řádka je směrování (odkud → kam), druhá časy; ikona i pomlčka leží
 * přes obě řádky. Navazující linka (spojený let / svoz) je menší a nese půlkolečko na té hraně,
 * kde leží hlavní doprava — tak je vidět, na co navazuje.
 *
 * U chybějícího spoje se směr neví, takže se ukáže jen ikona a poznámka, žádné kódy měst.
 */
export function TransportChip({ leg }: TransportChipProps) {
  const { t } = useTranslation('venua-planner');
  const isFeeder = leg.kind === 'feeder';
  const isMissing = leg.state === 'missing';
  const Icon = leg.mode === 'air' ? PlaneIcon : BusIcon;

  const box = (
    <span
      className={cn(
        'rounded-phx box-border inline-flex h-7 max-w-full items-center overflow-hidden border whitespace-nowrap',
        isFeeder ? 'gap-[3px] px-[3px] text-[9.5px]' : 'gap-phx-2 px-[5px] text-[10.5px]',
        STATE_BOX[leg.state]
      )}
    >
      {!isMissing && (
        <Endpoint
          code={leg.from ?? '?'}
          time={{
            label: leg.departure?.label ?? 'hh:mm',
            known: Boolean(leg.departure),
            offset: offsetLabel(leg.departure?.dayOffset ?? 0),
          }}
          align="end"
        />
      )}
      <span className={cn('shrink-0', iconColor(leg))}>
        <Icon size={isFeeder ? 11 : 16} />
      </span>
      {!isMissing && (
        <Endpoint
          code={leg.to ?? '?'}
          time={{
            label: leg.arrival?.label ?? 'hh:mm',
            known: Boolean(leg.arrival),
            offset: offsetLabel(leg.arrival?.dayOffset ?? 0),
          }}
          align="start"
        />
      )}
      {isMissing && (
        <span className="shrink-0">
          {leg.mode === 'air' ? t('calendar.bar.missingFlight') : t('calendar.bar.missingLeg')}
        </span>
      )}
    </span>
  );

  if (!isFeeder) return box;

  const notchOnEnd = leg.direction === 'out';
  return (
    <span
      className={cn(
        'relative inline-flex max-w-full items-center',
        notchOnEnd ? 'mr-[7px]' : 'ml-[7px]'
      )}
    >
      {box}
      <span
        className={cn(
          'absolute top-2 box-border h-3 w-2 border',
          notchOnEnd
            ? '-right-1.5 rounded-r-[6px] border-l-0'
            : '-left-1.5 rounded-l-[6px] border-r-0',
          STATE_NOTCH[leg.state]
        )}
      />
    </span>
  );
}
